import { Quote } from '@/model/quotesModel';

import { useMemo } from 'react';

import { isEqual } from 'lodash';
import {
  adjustQuoteForPremium,
  adjustQuotesForPremium,
  isPremiumAsRequested,
} from '@/util/quoteAdjustment';
import { enrichQuotesWithROI, type EnrichedQuote } from '@shared';
import {
  inRange,
  matchesTab,
  getInflationProtection,
  getBenefitsDuration,
  matchesInflationType,
  matchesMinimumInflation,
  matchesBenefitPaymentType,
  matchesCouplesDiscount,
  matchesFeatureBenefits,
  MAX_DURATION_YEARS,
} from '@/features/shop-insurance/lib/filter-matchers';
import { calculateGapFillPremium } from '@/features/shop-insurance/lib/coverage-gap-utils';
import type {
  CombinedFilterType,
  FilterName,
  InflationTypeFilterValue,
  InflationType,
} from '@/features/shop-insurance/types';
import type { BenefitPaymentTypeFilter } from '@/features/shop-insurance/stores/benefit-type-filter-store';

interface UseAdjustedQuotesParams {
  selectedPremium: number;
  client: Client | undefined;
  selectedTab: ShopInsuranceTab;
  originalQuotes: Quote[] | undefined;
  selectedBenefitDuration: CombinedFilterType;
  /** Minimum inflation protection %; 0 means unfiltered. Used when `useMinimumInflation`. */
  selectedInflationProtection: number;
  /** Legacy AOB/COB range; used when `useMinimumInflation` is false. */
  selectedInflationProtectionRange: CombinedFilterType;
  /** `inflationProtectionMinimum` flag — picks minimum-% matching over legacy range matching. */
  useMinimumInflation: boolean;
  selectedInflationTypeFilter: InflationTypeFilterValue;
  returnOnInvestmentCalculationType: ReturnOnInvestmentCalculationType;
  selectedFeatureBenefitsFilters?: string[];
  selectedBenefitPaymentType?: BenefitPaymentTypeFilter;
  includeCouplesDiscount?: boolean;
  showLifeFilters?: boolean;
  isGapFillMode?: boolean;
  totalCost?: number;
}

interface UseAdjustedQuotesReturn {
  activeFilters: FilterName[];
  originalTotalQuotes: number;
  adjustedQuotes: EnrichedQuote[];
}

const PREMIUM_ADJUSTMENT_THRESHOLD = 100;

export const MAX_INFLATION_PERCENT = 5;

const DEFAULT_FILTERS_VALUES = {
  benefitDuration: [1, MAX_DURATION_YEARS],
  inflationProtection: 0,
  /** Legacy AOB/COB range bounds, used while `inflationProtectionMinimum` is off. */
  inflationProtectionRange: [0, MAX_INFLATION_PERCENT],
};

export const DEFAULT_FILTERS_STATE = {
  benefitDuration: {
    type: 'combined' as const,
    combined: DEFAULT_FILTERS_VALUES.benefitDuration,
    aob: DEFAULT_FILTERS_VALUES.benefitDuration,
    cob: DEFAULT_FILTERS_VALUES.benefitDuration,
  },
  inflationProtection: DEFAULT_FILTERS_VALUES.inflationProtection,
  inflationProtectionRange: {
    type: 'combined' as const,
    combined: DEFAULT_FILTERS_VALUES.inflationProtectionRange,
    aob: DEFAULT_FILTERS_VALUES.inflationProtectionRange,
    cob: DEFAULT_FILTERS_VALUES.inflationProtectionRange,
  },
  inflationTypeFilter: 'all' as const,
};

function isFilterApplied(
  filter: CombinedFilterType | null | undefined,
  defaultState: CombinedFilterType,
) {
  return !!filter && !isEqual(filter, defaultState);
}

/**
 * Orders quotes the product could actually honor at the selected premium above the ones it
 * could not, then by ROI within each group.
 *
 * A clamped or pinned quote is displayed at a different premium than the one requested, so
 * comparing its ROI head-to-head with an honored quote compares two different purchases.
 * Those quotes still appear — they just sort below, behind the amber badge that explains why.
 */
export function compareByPremiumHonoredThenROI(quoteA: EnrichedQuote, quoteB: EnrichedQuote) {
  const honoredDiff =
    Number(isPremiumAsRequested(quoteB as Quote)) - Number(isPremiumAsRequested(quoteA as Quote));
  if (honoredDiff !== 0) return honoredDiff;

  return (quoteB.roi ?? 0) - (quoteA.roi ?? 0);
}

function shouldAdjustPremium(quotes: Quote[], selectedPremium: number) {
  return quotes.some(
    quote => Math.abs(quote.premiumAmount - selectedPremium) > PREMIUM_ADJUSTMENT_THRESHOLD,
  );
}

/**
 * Legacy AOB/COB range matching, retained for when `inflationProtectionMinimum` is off.
 * Deletable together with the range UI once the flag is removed.
 */
function matchesInflationRange(quote: Quote, filter: CombinedFilterType) {
  const { hasSingle, single, aob, cob } = getInflationProtection(quote);

  if (filter.type === 'separate') {
    const [minAob, maxAob] = filter.aob;
    const [minCob, maxCob] = filter.cob;
    const rangesAreEqual = minAob === minCob && maxAob === maxCob;

    if (!rangesAreEqual) {
      if (hasSingle && typeof single === 'number') {
        return inRange(single, minAob, maxAob) && inRange(single, minCob, maxCob);
      }
      return inRange(aob, minAob, maxAob) && inRange(cob, minCob, maxCob);
    }
  }

  const [min, max] = filter.type === 'separate' ? filter.aob : filter.combined;

  if (hasSingle && typeof single === 'number') {
    return inRange(single, min, max);
  }

  const isHybrid = aob !== cob;

  if (isHybrid) return inRange(aob, min, max) || inRange(cob, min, max);

  return inRange(aob, min, max);
}

function matchesBenefitsDuration(quote: Quote, filter: CombinedFilterType) {
  const { aobYears, cobYears, totalYears } = getBenefitsDuration(quote);

  if (filter.type === 'separate') {
    const [minAob, maxAob] = filter.aob;
    const [minCob, maxCob] = filter.cob;

    if (aobYears || cobYears) {
      return (
        inRange(aobYears, minAob, maxAob, MAX_DURATION_YEARS) &&
        inRange(cobYears, minCob, maxCob, MAX_DURATION_YEARS)
      );
    }

    const minTotal = minAob + minCob;
    const maxTotal = maxAob + maxCob;

    return inRange(totalYears, minTotal, maxTotal, MAX_DURATION_YEARS * 2);
  }

  const [minCombined, maxCombined] = filter.combined;

  if (aobYears || cobYears) {
    return inRange(aobYears + cobYears, minCombined, maxCombined, MAX_DURATION_YEARS);
  }

  return inRange(totalYears, minCombined, maxCombined, MAX_DURATION_YEARS);
}

function toInflationTypes(filter: InflationTypeFilterValue): InflationType[] | null {
  if (filter === 'all') return null;
  return [filter];
}

export function getActiveQuoteFilters(
  inflationFilter: number,
  inflationRangeFilter: CombinedFilterType,
  useMinimumInflation: boolean,
  durationFilter: CombinedFilterType,
  inflationTypeFilter: InflationTypeFilterValue,
  showLifeFilters = true,
): FilterName[] {
  if (!showLifeFilters) return [];

  const activeFilters: FilterName[] = ['premium'];

  const inflationApplied = useMinimumInflation
    ? inflationFilter > DEFAULT_FILTERS_STATE.inflationProtection
    : isFilterApplied(inflationRangeFilter, DEFAULT_FILTERS_STATE.inflationProtectionRange);

  if (inflationApplied) {
    activeFilters.push('inflationProtection');
  }

  if (inflationTypeFilter !== 'all') {
    activeFilters.push('inflationType');
  }

  if (isFilterApplied(durationFilter, DEFAULT_FILTERS_STATE.benefitDuration)) {
    activeFilters.push('benefitDuration');
  }

  return activeFilters;
}

function adjustQuotesToFillGap(
  quotes: Quote[],
  totalCost: number,
  client: Client,
  calculationType: ReturnOnInvestmentCalculationType,
): EnrichedQuote[] {
  // First enrichment: get coverage at each quote's original premium to use as the gap-fill baseline.
  const quotesWithBaseCoverage = enrichQuotesWithROI(quotes, client, calculationType);

  const quotesScaledToGapPremium = quotesWithBaseCoverage.map(quote => {
    const baseCoverage = quote.projectedCostCoverage ?? 0;
    if (baseCoverage <= 0) return quote;
    const gapFillPremium = calculateGapFillPremium(
      quote,
      totalCost,
      baseCoverage,
      quote.maxAchievableCoverage ?? totalCost,
    );
    return adjustQuoteForPremium(quote, gapFillPremium);
  });

  // Second enrichment: compute actual coverage at the gap-fill premiums for display.
  return enrichQuotesWithROI(quotesScaledToGapPremium, client, calculationType, {
    includeOriginalROIValues: true,
  });
}

function useAdjustedQuotes({
  client,
  selectedTab,
  originalQuotes,
  selectedPremium,
  selectedBenefitDuration,
  selectedInflationProtection,
  selectedInflationProtectionRange,
  useMinimumInflation,
  selectedInflationTypeFilter,
  returnOnInvestmentCalculationType,
  selectedFeatureBenefitsFilters = [],
  selectedBenefitPaymentType = 'all',
  includeCouplesDiscount = true,
  showLifeFilters = true,
  isGapFillMode = false,
  totalCost = 0,
}: UseAdjustedQuotesParams): UseAdjustedQuotesReturn {
  return useMemo(() => {
    const activeFilters = getActiveQuoteFilters(
      selectedInflationProtection,
      selectedInflationProtectionRange,
      useMinimumInflation,
      selectedBenefitDuration,
      selectedInflationTypeFilter,
      showLifeFilters,
    );

    if (!originalQuotes?.length || !client) {
      return { adjustedQuotes: [], activeFilters, originalTotalQuotes: 0 };
    }

    const inflationTypes = toInflationTypes(selectedInflationTypeFilter);

    const filteredQuotes = originalQuotes.filter(
      quote =>
        matchesTab(quote, selectedTab) &&
        (useMinimumInflation
          ? matchesMinimumInflation(quote, selectedInflationProtection)
          : matchesInflationRange(quote, selectedInflationProtectionRange)) &&
        (!inflationTypes || matchesInflationType(quote, inflationTypes)) &&
        matchesBenefitsDuration(quote, selectedBenefitDuration) &&
        matchesFeatureBenefits(quote, selectedFeatureBenefitsFilters) &&
        matchesBenefitPaymentType(quote, selectedBenefitPaymentType) &&
        matchesCouplesDiscount(quote, includeCouplesDiscount),
    );

    if (!filteredQuotes.length) {
      return { adjustedQuotes: [], activeFilters, originalTotalQuotes: originalQuotes.length };
    }

    const hasNonPremiumFilter = activeFilters.some(filter => filter !== 'premium');
    const shouldRepriceQuotes =
      hasNonPremiumFilter || shouldAdjustPremium(filteredQuotes, selectedPremium);

    let withROI: EnrichedQuote[];
    if (isGapFillMode && totalCost > 0) {
      // adjustQuotesToFillGap handles both enrichment passes internally, so we skip
      // the outer enrichQuotesWithROI to avoid a third enrichment at the gap-fill premiums.
      withROI = adjustQuotesToFillGap(
        filteredQuotes,
        totalCost,
        client,
        returnOnInvestmentCalculationType,
      );
    } else {
      const quotesToProcess = shouldRepriceQuotes
        ? adjustQuotesForPremium(filteredQuotes, selectedPremium)
        : filteredQuotes;
      withROI = enrichQuotesWithROI(quotesToProcess, client, returnOnInvestmentCalculationType, {
        includeOriginalROIValues: true,
      });
    }

    const adjustedQuotes = withROI
      .filter(quote => (quote.projectedCostCoverage ?? 0) > 0)
      .sort(compareByPremiumHonoredThenROI);

    return {
      adjustedQuotes,
      activeFilters,
      originalTotalQuotes: originalQuotes.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    client,
    selectedTab,
    originalQuotes,
    selectedPremium,
    returnOnInvestmentCalculationType,
    selectedBenefitDuration.type,
    selectedBenefitDuration.combined,
    selectedBenefitDuration.aob,
    selectedBenefitDuration.cob,
    selectedInflationProtection,
    selectedInflationProtectionRange.type,
    selectedInflationProtectionRange.combined,
    selectedInflationProtectionRange.aob,
    selectedInflationProtectionRange.cob,
    useMinimumInflation,
    selectedInflationTypeFilter,
    selectedFeatureBenefitsFilters,
    selectedBenefitPaymentType,
    includeCouplesDiscount,
    showLifeFilters,
    isGapFillMode,
    totalCost,
  ]);
}

export { DEFAULT_FILTERS_VALUES, useAdjustedQuotes };
